import apiClient from './axios';

/**
 * 레이드 정보
 */
export interface Raid {
  raidTitle: string;
  raidName: string;
  levelCut: number;
  atkNum: number;
  supNum: number;
  seq: number;
}

/**
 * 스케줄 생성 요청
 */
export interface ScheduleCreateRequest {
  raidTitle: string;
  content: string;
  startTime: string;  // "yyyy-MM-dd HH:mm" 형식
  isDouble: number;   // 0 or 1
}

/**
 * 스케줄 수정 요청
 */
export interface ScheduleUpdateRequest {
  content: string;
  startTime: string;  // "yyyy-MM-dd HH:mm" 형식
}

/**
 * 스케줄 참여 요청
 */
export interface ScheduleEnrollRequest {
  characterName: string;
  atkPossible: number;  // 0 or 1
  supPossible: number;  // 0 or 1
  doubleType?: string;  // "0000" ~ "1111" (atkAtk, atkSup, supAtk, supSup)
}

/**
 * 스케줄 정보 (조회용)
 */
export interface Schedule {
  scheduleId: number;
  memberId: string;
  raidTitle: string;
  content: string;
  startTime: string;
  isDouble: number;
  nickname: string;
  levelCut: number;
  atkNum: number;
  supNum: number;
  enrollMemberNum: number;
  mySchedule: number;  // 1: 내가 생성한 스케줄
  isEnrolled: number;  // 1: 참여 중
  enrolledCharacterName?: string;  // 내가 참여한 경우, 참여한 캐릭터 닉네임
}

/**
 * 스케줄 참여자 정보
 */
export interface ScheduleMember {
  id: number;
  scheduleId: number;
  nickname: string;
  characterClass: string;
  itemLevel: string;
  classType: string;
  supPossible: number;
  atkPossible: number;
  doubleType: string;
}

/**
 * 스케줄 상세 정보 (RaidSchedule 엔티티)
 */
export interface RaidSchedule {
  scheduleId: number;
  memberId: string;
  raidTitle: string;
  content: string;
  startTime: string;
  createdAt: string;
  isDouble: number;
}

/**
 * 참여 가능 여부 응답
 */
export interface AvailabilityCheck {
  isMemberApplied: number;  // 1: 이미 참여 중
  isExceeded: number;       // 1: 인원 초과
}

/**
 * 레이드 목록 조회
 */
export const getRaids = async (): Promise<Raid[]> => {
  const response = await apiClient.get<Raid[]>('/api/schedules/raids');
  return response.data;
};

/**
 * 주간 스케줄 조회
 * @param wedDate 수요일 날짜 (yyyy-MM-dd)
 * @param tueDate 화요일 날짜 (yyyy-MM-dd)
 */
export const getScheduleThisWeek = async (
  wedDate: string,
  tueDate: string
): Promise<Schedule[]> => {
  const response = await apiClient.get<{ raidSchedules: Schedule[] }>(
    '/api/schedules',
    {
      params: { wedDate, tueDate },
    }
  );
  return response.data.raidSchedules || [];
};

/**
 * 스케줄 상세 조회 (수정용)
 * @param scheduleId 스케줄 ID
 */
export const getScheduleById = async (scheduleId: number): Promise<RaidSchedule> => {
  const response = await apiClient.get<{ raidSchedule: RaidSchedule }>(
    `/api/schedules/${scheduleId}`
  );
  return response.data.raidSchedule;
};

/**
 * 스케줄 참여자 목록 조회
 * @param scheduleId 스케줄 ID
 */
export const getScheduleMembers = async (scheduleId: number): Promise<ScheduleMember[]> => {
  const response = await apiClient.get<{ raidScheduleMembers: ScheduleMember[] }>(
    `/api/schedules/${scheduleId}/members`
  );
  return response.data.raidScheduleMembers;
};

/**
 * 스케줄 참여 가능 여부 체크
 * @param scheduleId 스케줄 ID
 * @param characterName 캐릭터 이름
 */
export const checkAvailability = async (
  scheduleId: number,
  characterName: string
): Promise<AvailabilityCheck> => {
  const response = await apiClient.get<AvailabilityCheck>(
    `/api/schedules/${scheduleId}/availability`,
    {
      params: { characterName },
    }
  );
  return response.data;
};

/**
 * 스케줄 생성
 * @param request 스케줄 생성 요청
 */
export const createSchedule = async (request: ScheduleCreateRequest) => {
  const response = await apiClient.post<{
    success: boolean;
    message: string;
  }>('/api/schedules', request);
  return response.data;
};

/**
 * 스케줄 수정
 * @param scheduleId 스케줄 ID
 * @param request 스케줄 수정 요청
 */
export const updateSchedule = async (
  scheduleId: number,
  request: ScheduleUpdateRequest
) => {
  const response = await apiClient.put<{
    success: boolean;
    message: string;
  }>(`/api/schedules/${scheduleId}`, request);
  return response.data;
};

/**
 * 스케줄 삭제
 * @param scheduleId 스케줄 ID
 */
export const deleteSchedule = async (scheduleId: number) => {
  const response = await apiClient.delete<{
    success: boolean;
    message: string;
  }>(`/api/schedules/${scheduleId}`);
  return response.data;
};

/**
 * 스케줄 참여
 * @param scheduleId 스케줄 ID
 * @param request 스케줄 참여 요청
 */
export const enrollInSchedule = async (
  scheduleId: number,
  request: ScheduleEnrollRequest
) => {
  const response = await apiClient.post<{
    success: boolean;
    message: string;
  }>(`/api/schedules/${scheduleId}/enroll`, request);
  return response.data;
};

/**
 * 스케줄 참여 정보 수정 (캐릭터 변경)
 * @param scheduleId 스케줄 ID
 * @param request 수정할 참여 정보
 */
export const updateEnrollment = async (
  scheduleId: number,
  request: ScheduleEnrollRequest
) => {
  const response = await apiClient.put<{
    success: boolean;
    message: string;
  }>(`/api/schedules/${scheduleId}/enroll`, request);
  return response.data;
};

/**
 * 스케줄 참여 취소
 * @param scheduleId 스케줄 ID
 */
export const cancelEnrollment = async (scheduleId: number) => {
  const response = await apiClient.delete<{
    success: boolean;
    message: string;
  }>(`/api/schedules/${scheduleId}/enroll`);
  return response.data;
};

/**
 * 스케줄 참여자 강제 삭제 (방장 권한)
 * @param memberId 참여자 ID
 */
export const deleteScheduleMember = async (memberId: number) => {
  const response = await apiClient.delete<{
    success: boolean;
    result: number;
  }>(`/api/schedules/members/${memberId}`);
  return response.data;
};
